import React, { useMemo, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useMutation, useQuery } from 'react-query';
import { getWhatsAppTemplates, sendWhatsAppMessage } from '../../../../../../../../services/whatsappIntegrationService';

interface WhatsAppSendProps {
  companyId: string;
}

interface WhatsAppTemplateComponent {
  type: string;
  text?: string;
  format?: string;
}

interface WhatsAppTemplate {
  id: string;
  name: string;
  language: string;
  status: string;
  category: string;
  components?: WhatsAppTemplateComponent[];
}

interface WhatsAppTemplatesResponse {
  data: WhatsAppTemplate[];
  paging?: any;
}

export type ParsedTemplateSendParameters = {
  header_parameters: any[];
  body_parameters: any[];
  footer_parameters: any[];
};

const parseTemplateParametersInput = (value: string): ParsedTemplateSendParameters => {
  const raw = value?.trim() || '[]';
  const parsed = JSON.parse(raw);

  if (Array.isArray(parsed)) {
    return {
      header_parameters: [],
      body_parameters: parsed,
      footer_parameters: []
    };
  }

  if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
    return {
      header_parameters: Array.isArray(parsed.header) ? parsed.header : [],
      body_parameters: Array.isArray(parsed.body)
        ? parsed.body
        : Array.isArray(parsed.parameters)
          ? parsed.parameters
          : [],
      footer_parameters: Array.isArray(parsed.footer) ? parsed.footer : []
    };
  }

  throw new Error('Use [] para só body ou {"header":[],"body":[],"footer":[]}');
};

const sendSchema = Yup.object().shape({
  to: Yup.string().required('Destinatário é obrigatório'),
  template_name: Yup.string().required('Template é obrigatório'),
  language_code: Yup.string().required('Idioma é obrigatório'),
  parameters_input: Yup.string()
    .required('Parâmetros são obrigatórios')
    .test(
      'valid-template-parameters',
      'JSON inválido: use [] para só body ou {"header":[],"body":[],"footer":[]}',
      (value) => {
        try {
          parseTemplateParametersInput(value || '[]');
          return true;
        } catch (_error) {
          return false;
        }
      }
    )
});

export function WhatsAppSend({ companyId }: WhatsAppSendProps) {
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const { data: templatesResponse, isLoading: isLoadingTemplates, error: templatesError } = useQuery<WhatsAppTemplatesResponse>(
    ['whatsappTemplates', companyId],
    () => getWhatsAppTemplates(companyId),
    {
      enabled: !!companyId
    }
  );
  const approvedTemplates = useMemo(
    () => (templatesResponse?.data || []).filter((template) => template.status?.toUpperCase() === 'APPROVED'),
    [templatesResponse]
  );
  const hasTemplatesError = Boolean(templatesError);

  const sendMutation = useMutation(sendWhatsAppMessage, {
    onSuccess: () => {
      formik.resetForm();
      setSelectedTemplateId('');
      // toggleToast('Mensagem enviada com sucesso!', 'success');
    },
    onError: (error) => {
      console.error('Erro ao enviar mensagem:', error);
      // toggleToast('Erro ao enviar mensagem', 'error');
    }
  });

  const formik = useFormik({
    initialValues: {
      to: '',
      template_name: '',
      language_code: '',
      parameters_input: '[]'
    },
    validationSchema: sendSchema,
    onSubmit: (values) => {
      let parsed: ParsedTemplateSendParameters;

      try {
        parsed = parseTemplateParametersInput(values.parameters_input);
      } catch (error) {
        formik.setFieldError('parameters_input', error instanceof Error ? error.message : 'JSON inválido');
        return;
      }

      const messageData = {
        to: values.to,
        template_name: values.template_name,
        language_code: values.language_code,
        header_parameters: parsed.header_parameters,
        body_parameters: parsed.body_parameters,
        footer_parameters: parsed.footer_parameters,
        companyId
      };
      sendMutation.mutate(messageData);
    }
  });

  const selectedTemplate = approvedTemplates.find((template) => template.id === selectedTemplateId);

  const handleTemplateChange = (templateId: string) => {
    setSelectedTemplateId(templateId);

    const template = approvedTemplates.find((item) => item.id === templateId);

    formik.setFieldValue('template_name', template?.name || '');
    formik.setFieldValue('language_code', template?.language || '');
  };

  return (
    <div>
      <h5 className="mb-4">Enviar Notificação WhatsApp</h5>

      <form onSubmit={formik.handleSubmit}>
        <div className="card">
          <div className="card-body">
            <div className="row">
              <div className="col-md-6">
                <label className="form-label">Número do Destinatário</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="5511999999999"
                  {...formik.getFieldProps('to')}
                />
                {formik.touched.to && formik.errors.to && (
                  <div className="text-danger small">{formik.errors.to}</div>
                )}
              </div>

              <div className="col-md-6">
                <label className="form-label">Template</label>
                <select
                  className="form-control"
                  name="selected_template"
                  value={selectedTemplateId}
                  onChange={(e) => handleTemplateChange(e.target.value)}
                  onBlur={() => formik.setFieldTouched('template_name', true)}
                  disabled={isLoadingTemplates}
                >
                  <option value="">Selecione um template</option>
                  {approvedTemplates.map((template) => (
                    <option key={template.id} value={template.id}>
                      {template.name} ({template.language})
                    </option>
                  ))}
                </select>
                {formik.touched.template_name && formik.errors.template_name && (
                  <div className="text-danger small">{formik.errors.template_name}</div>
                )}
                {hasTemplatesError && (
                  <div className="text-danger small mt-1">
                    Erro ao carregar templates aprovados.
                  </div>
                )}
                {!isLoadingTemplates && !hasTemplatesError && approvedTemplates.length === 0 && (
                  <div className="text-muted small mt-1">
                    Nenhum template aprovado encontrado para envio.
                  </div>
                )}
              </div>
            </div>

            <div className="row mt-3">
              <div className="col-md-6">
                <label className="form-label">Idioma</label>
                <select
                  className="form-control"
                  {...formik.getFieldProps('language_code')}
                  disabled={!selectedTemplate}
                >
                  <option value="">Selecione um template primeiro</option>
                  <option value="pt_BR">Português (BR)</option>
                  <option value="en_US">English (US)</option>
                  <option value="es_ES">Español</option>
                </select>
                {formik.touched.language_code && formik.errors.language_code && (
                  <div className="text-danger small">{formik.errors.language_code}</div>
                )}
              </div>
            </div>

            {selectedTemplate && (
              <div className="alert alert-info mt-4">
                <div><strong>Template selecionado:</strong> {selectedTemplate.name}</div>
                <div><strong>Categoria:</strong> {selectedTemplate.category}</div>
                <div><strong>Idioma:</strong> {selectedTemplate.language}</div>
                <div><strong>Componentes:</strong> {selectedTemplate.components?.map((component) => component.type).join(', ') || 'Nenhum'}</div>
              </div>
            )}

            <div className="mt-4">
              <label className="form-label">Parâmetros (JSON)</label>
              <textarea
                className="form-control"
                rows={6}
                placeholder='[] ou {"header":[],"body":[],"footer":[]}'
                {...formik.getFieldProps('parameters_input')}
              />
              {formik.touched.parameters_input && formik.errors.parameters_input && (
                <div className="text-danger small">{formik.errors.parameters_input}</div>
              )}
              <small className="text-muted">
                Apenas body: array no formato da Meta, ex. <code>[]</code> ou{' '}
                <code>[{`{"type":"text","text":"João"}`}]</code>. Com cabeçalho e rodapé:{' '}
                <code>{`{"header":[...],"body":[...],"footer":[...]}`}</code>.
              </small>
            </div>

            <div className="d-flex justify-content-end mt-4">
              <button
                type="submit"
                className="btn btn-primary"
                disabled={sendMutation.isLoading}
              >
                {sendMutation.isLoading ? 'Enviando...' : 'Enviar Mensagem'}
              </button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}